import type { Breakpoint, Button } from "@mui/material";
import { type ReactElement, type ReactNode, createContext, useContext, useState } from "react";

interface IRegisteredButton {
  id: string;
  button: ReactElement<typeof Button>;
  position: number;
}

interface IResponsiveButtonContextProps {
  buttons: IRegisteredButton[];
  registerButton: (id: string, button: ReactElement<typeof Button>, position: number) => void;
  unregisterButton: (id: string) => void;
  breakpoint: Breakpoint;
}

interface IResponsiveButtonProviderProps {
  breakpoint?: Breakpoint;
  children: ReactNode;
}

const ButtonContext = createContext<IResponsiveButtonContextProps | null>(null);

export const ResponsiveButtonProvider = ({ breakpoint = "desktop", children }: IResponsiveButtonProviderProps) => {
  const [buttons, setButtons] = useState<IRegisteredButton[]>([]);

  const registerButton = (id: string, button: ReactElement<typeof Button>, position: number) => {
    setButtons((prevButtons) => {
      const newButtonList = prevButtons.filter((el) => el.id !== id);
      newButtonList.push({ id, button, position });
      return newButtonList.toSorted((a, b) => a.position - b.position);
    });
  };

  const unregisterButton = (id: string) => {
    setButtons((prevButtons) => {
      const newButtonList = prevButtons.filter((el) => el.id !== id);
      return newButtonList.toSorted((a, b) => a.position - b.position);
    });
  };

  return (
    <ButtonContext.Provider value={{ buttons, registerButton, unregisterButton, breakpoint }}>
      {children}
    </ButtonContext.Provider>
  );
};

export const useResponsiveButtons = (): IResponsiveButtonContextProps => {
  const context = useContext(ButtonContext);
  if (!context) {
    throw new Error("useButtons must be used within a ResponsiveButtonProvider");
  }
  return context;
};
